import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CollectionRouting, PaymentSelection } from '../types/schedule.types';
import { collectionRoutingEqual } from '../components/steps/PaymentStep/derive';
import { NO_LINK_DELIVERY, type LinkDeliveryChoice } from '../types/linkDelivery.types';
import type { QuoteJourney } from './useElemisCheckout';
import type { QuoteSaveLaneApi } from './useQuoteSaveLane';
import {
    applyCollectionRouting,
    paymentCollectionModalMode,
    type PaymentCollectionAction,
    type PaymentCollectionModalMode,
} from '../utils/paymentCollectionGate';

type GateSaveLane = Pick<
    QuoteSaveLaneApi,
    'flush' | 'settled' | 'guard' | 'isUnsavedNow' | 'isBusy' | 'laneDraftId'
>;

export interface UsePaymentCollectionGateOptions {
    paymentSelection: PaymentSelection | undefined;
    /** Whether this sale's dealer can collect payment on their own facility. */
    canSelfCollect: boolean;
    /** Whether the caller holds `agreement.initiate_application`. */
    canInitiate: boolean;
    /** A checkout URL is held and not failed: continuing reopens it and creates nothing. */
    liveCheckoutHeld: boolean;
    /** Derived from the committed selection by `useElemisCheckout`; flips to
     *  `motorplan_collects` once an Elemis-bound routing is committed. */
    journey: QuoteJourney;
    updatePaymentSelection: (selection: PaymentSelection) => void;
    saveLane: GateSaveLane;
    initiateCheckout: (delivery: LinkDeliveryChoice) => void;
    /** Supersedes a dead checkout with its last delivery choice. */
    retryCheckout: () => void;
    completeSale: () => Promise<void> | void;
}

interface PendingChoice {
    draftId: string | null;
    routing: CollectionRouting | undefined;
    action: PaymentCollectionAction | 'retry';
    delivery: LinkDeliveryChoice;
    stage: 'confirmed' | 'saving' | 'awaiting-journey';
}

export interface PaymentCollectionGate {
    isOpen: boolean;
    /** What the modal offers for this selection and caller; `none` means it never opens. */
    mode: PaymentCollectionModalMode;
    /** True from the save starting until the terminal action runs, or while the save lane is busy. */
    isBusy: boolean;
    /** The summary "Continue to payment" handler: opens the modal unless there is nothing to
     *  offer or a held checkout can simply be reopened. */
    requestContinue: () => void;
    confirm: (
        routing: CollectionRouting | undefined,
        action: PaymentCollectionAction,
        delivery: LinkDeliveryChoice,
    ) => void;
    cancel: () => void;
    /** The "Retry payment" handler: settles the save lane clean before superseding the dead checkout. */
    retry: () => void;
}

/** Owns the post-summary who-collects gate: commits the routing, settles the save lane clean, then
 *  completes a dealer paid-in-full or initiates the checkout once `journey` has re-derived. */
export function usePaymentCollectionGate(
    options: UsePaymentCollectionGateOptions,
): PaymentCollectionGate {
    const {
        paymentSelection,
        canSelfCollect,
        canInitiate,
        liveCheckoutHeld,
        journey,
        updatePaymentSelection,
        saveLane,
        initiateCheckout,
        retryCheckout,
        completeSale,
    } = options;

    const [isOpen, setIsOpen] = useState(false);
    const [pendingChoice, setPendingChoice] = useState<PendingChoice | null>(null);
    const pending = pendingChoice?.draftId === saveLane.laneDraftId ? pendingChoice : null;

    const mode = useMemo(
        () => paymentCollectionModalMode(paymentSelection, canSelfCollect, canInitiate),
        [paymentSelection, canSelfCollect, canInitiate],
    );

    const { guard, settled, flush, isUnsavedNow } = saveLane;
    const settleLane = useCallback(
        (routingChanged: boolean, onClean?: () => void) =>
            guard(async (ensureOwned) => {
                await settled();
                ensureOwned();
                if (routingChanged || isUnsavedNow()) {
                    await flush({ quiet: true });
                }
                await settled();
                ensureOwned();
                if (isUnsavedNow()) {
                    return false;
                }
                onClean?.();
                return true;
            })
                // The lane hook has already surfaced a failed save; there is nothing more to add.
                .catch(() => false),
        [guard, settled, flush, isUnsavedNow],
    );

    const advanceChoice = useCallback(
        async (choice: PendingChoice, routingChanged: boolean) => {
            // Completion runs inside the guard, with no await after its last ownership check.
            const onClean = choice.action === 'complete' ? () => void completeSale() : undefined;
            const clean = await settleLane(routingChanged, onClean);
            if (!clean || choice.action === 'complete') {
                setPendingChoice((current) => (current === choice ? null : current));
                return;
            }
            setPendingChoice((current) =>
                current === choice ? { ...choice, stage: 'awaiting-journey' } : current,
            );
        },
        [settleLane, completeSale],
    );

    const runChoice = useCallback(
        (choice: PendingChoice, selection: PaymentSelection, routingChanged: boolean) => {
            if (routingChanged) {
                updatePaymentSelection(selection);
            }
            return advanceChoice(choice, routingChanged);
        },
        [updatePaymentSelection, advanceChoice],
    );

    useEffect(() => {
        if (pending?.stage !== 'awaiting-journey') {
            return;
        }
        if (journey !== 'motorplan_collects') {
            if (pending.action === 'retry') {
                setPendingChoice(null);
            }
            return;
        }
        // A save requested since the lane settled clean (e.g. add-on seeding) must land first.
        if (isUnsavedNow()) {
            const choice: PendingChoice = { ...pending, stage: 'saving' };
            setPendingChoice(choice);
            void advanceChoice(choice, false);
            return;
        }
        setPendingChoice(null);
        if (pending.action === 'retry') {
            retryCheckout();
            return;
        }
        initiateCheckout(pending.delivery);
    }, [pending, journey, initiateCheckout, retryCheckout, isUnsavedNow, advanceChoice]);

    const retryInFlightRef = useRef(false);
    useEffect(() => {
        if (pending === null) {
            retryInFlightRef.current = false;
        }
    }, [pending, pendingChoice]);

    const requestContinue = useCallback(() => {
        if (mode.kind === 'none') {
            return;
        }
        if (mode.kind === 'motorplan_only' && liveCheckoutHeld) {
            initiateCheckout(NO_LINK_DELIVERY);
            return;
        }
        setIsOpen(true);
    }, [mode, liveCheckoutHeld, initiateCheckout]);

    useEffect(() => {
        if (isOpen || pending?.stage !== 'confirmed' || !paymentSelection) {
            return;
        }
        const selection = applyCollectionRouting(paymentSelection, pending.routing);
        const routingChanged = !collectionRoutingEqual(
            selection.collectionRouting,
            paymentSelection.collectionRouting,
        );
        const choice: PendingChoice = { ...pending, stage: 'saving' };
        setPendingChoice(choice);
        void runChoice(choice, selection, routingChanged);
    }, [isOpen, pending, paymentSelection, runChoice]);

    const confirm = useCallback(
        (routing: CollectionRouting | undefined, action: PaymentCollectionAction, delivery: LinkDeliveryChoice) => {
            setIsOpen(false);
            setPendingChoice({
                draftId: saveLane.laneDraftId,
                routing,
                action,
                delivery,
                stage: 'confirmed',
            });
        },
        [saveLane.laneDraftId],
    );

    const cancel = useCallback(() => setIsOpen(false), []);

    const retry = useCallback(() => {
        if (journey !== 'motorplan_collects' || pending !== null || retryInFlightRef.current) {
            return;
        }
        retryInFlightRef.current = true;
        const choice: PendingChoice = {
            draftId: saveLane.laneDraftId,
            routing: paymentSelection?.collectionRouting,
            action: 'retry',
            delivery: NO_LINK_DELIVERY,
            stage: 'saving',
        };
        setPendingChoice(choice);
        void advanceChoice(choice, false);
    }, [journey, pending, saveLane.laneDraftId, paymentSelection, advanceChoice]);

    // Not busy while only confirmed: the closing modal restores focus to Continue first.
    return {
        isOpen,
        mode,
        isBusy: saveLane.isBusy || (pending !== null && pending.stage !== 'confirmed'),
        requestContinue,
        confirm,
        cancel,
        retry,
    };
}
